<template>
  <div class="h-screen min-h-screen lg:h-screen px-0 pt-0 pb-0 mx-0 my-0 box-border overflow-hidden bg-white">
    <v-row class="h-full min-h-screen lg:h-screen" no-gutters>
      <v-col class="lg:flex lg:flex-col lg:justify-center px-0 hidden relative h-full" cols="12" lg="6">
        <LoginCarousel />
        <!-- Vertical divider -->
        <div class="absolute right-0 top-0 bottom-0 w-[1px] bg-slate-800"></div>
      </v-col>

      <v-col cols="12" lg="6"
        class="bg-white h-screen overflow-y-auto relative pa-0 flex flex-col justify-between overflow-hidden"
        :class="$vuetify.breakpoint.smAndDown ? 'login-mobile-col' : ''">
        <!-- Background Watermark Circles & Dot Grid (Clipped in an absolute inset-0 layer to prevent scroll overflow) -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <!-- Top-Right Arc (~30% visible) -->
          <div
            class="absolute -top-48 -right-48 w-80 h-80 sm:w-96 sm:h-96 rounded-full bg-[#f0f7ff] pointer-events-none">
          </div>
          <!-- Top-Right 3x3 Dot Grid -->
          <div class="absolute top-4 right-4 pointer-events-none opacity-40">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="2" fill="#3B82F6" />
              <circle cx="24" cy="12" r="2" fill="#3B82F6" />
              <circle cx="36" cy="12" r="2" fill="#3B82F6" />
              <circle cx="12" cy="24" r="2" fill="#3B82F6" />
              <circle cx="24" cy="24" r="2" fill="#3B82F6" />
              <circle cx="36" cy="24" r="2" fill="#3B82F6" />
              <circle cx="12" cy="36" r="2" fill="#3B82F6" />
              <circle cx="24" cy="36" r="2" fill="#3B82F6" />
              <circle cx="36" cy="36" r="2" fill="#3B82F6" />
            </svg>
          </div>

          <!-- Bottom-Left Two Large Radius Circles (~30% visible side-by-side) -->
          <!-- Left Sweeping Arc -->
          <div
            class="absolute -bottom-64 -left-48 w-96 h-96 sm:w-[400px] sm:h-[400px] rounded-full bg-[#f4f9ff] pointer-events-none">
          </div>
          <!-- Right Adjacent Sweeping Arc -->
          <div
            class="absolute -bottom-60 -left-6 w-80 h-80 sm:w-[350px] sm:h-[350px] rounded-full bg-[#eaf4ff]/90 pointer-events-none">
          </div>
        </div>

        <!-- Top App Bar (Back Button when inside sub-views) -->
        <div v-if="landingView !== 'landing'" class="absolute top-4 left-4 sm:left-6 z-20">
          <button type="button" @click="handleBackNavigation"
            class="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold text-sm px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer select-none">
            <v-icon small class="text-gray-600">mdi-arrow-left</v-icon>
            <span>Back</span>
          </button>
        </div>

        <!-- MAIN INNER CONTAINER (exact clone of app max-w-sm sm:max-w-md my-auto) -->
        <div class="relative z-10"
          :class="$vuetify.breakpoint.smAndDown ? 'login-mobile-main' : 'w-full max-w-sm sm:max-w-md mx-auto my-auto px-4 sm:px-6 pt-12 pb-6'">
          <div class="w-full">
            <!-- Desktop version text -->
            <!-- <div class="d-none d-lg-flex justify-end text-xs text-gray-400 mb-2">
              <span>version {{ versionNumber }}</span>
            </div> -->

            <!-- Logo Section: Exact pixel placement as mobile app -->
            <div class="w-full d-flex align-center justify-center mb-6 flex-shrink-0 select-none">
              <img src="@/assets/img/CW_DEFAULT.png" alt="Cuality Works"
                class="w-36 sm:w-44 h-auto object-contain block pointer-events-none" />
            </div>

            <!-- ========================================================================= -->
            <!-- VIEW 1: DEFAULT LANDING SCREEN (Login as Employee, Admin, Create Company) -->
            <!-- ========================================================================= -->
            <transition name="fade-up" mode="out-in">
              <div v-if="landingView === 'landing'" key="landing-choice"
                class="w-full flex flex-col items-center text-center">
                <h1 class="text-heading-main font-extrabold text-gray-900 mb-2 tracking-tight">
                  Welcome back
                </h1>
                <p class="text-gray-500 text-normal leading-relaxed max-w-xs mb-8">
                  Sign in to continue to your workspace
                </p>

                <!-- 3 Actions: Login as Employee, Login as Admin, Create New Company -->
                <div class="w-full flex flex-col gap-3.5">
                  <!-- 1. Login as Employee (Blue Pill Button with Left Icon + Divider and Right Arrow) -->
                  <button type="button" @click="chooseLoginMode('employee')"
                    class="w-full h-13 px-5 py-3 bg-[#1877F2] hover:bg-[#166fe5] active:bg-[#1465d2] text-white rounded-full font-semibold text-subheading shadow-sm transition-all duration-200 flex items-center justify-between border-0 cursor-pointer select-none">
                    <div class="flex items-center">
                      <v-icon color="white" size="20">mdi-account-outline</v-icon>
                      <span class="h-6 w-[1.5px] bg-white/40 inline-block mx-2"></span>
                      <span class="text-white text-subheading font-semibold">Login as Employee</span>
                    </div>
                    <v-icon color="white" size="20">mdi-arrow-right</v-icon>
                  </button>

                  <!-- 2. Login as Admin (White Pill Button with Visible Light Blue Border) -->
                  <button type="button" @click="chooseLoginMode('admin')"
                    style="border: 1.5px solid #60a5fa !important;"
                    class="w-full h-13 px-5 py-3 bg-white hover:bg-blue-50/50 active:bg-blue-50 text-[#1877F2] rounded-full font-semibold text-subheading shadow-sm transition-all duration-200 flex items-center justify-between cursor-pointer select-none">
                    <div class="flex items-center">
                      <v-icon color="#1877F2" size="20">mdi-shield-check-outline</v-icon>
                      <span class="h-6 w-[1.5px] bg-[#1877F2]/30  inline-block mx-2"></span>
                      <span class="text-[#1877F2] text-subheading font-semibold">Login as Admin</span>
                    </div>
                    <v-icon color="#1877F2" size="20">mdi-arrow-right</v-icon>
                  </button>

                  <!-- or Divider -->
                  <div class="w-full flex items-center justify-center my-1.5 px-6">
                    <div class="flex-1 h-[1px] bg-gray-200"></div>
                    <span class="px-4 text-caption text-gray-400 font-medium lowercase">or</span>
                    <div class="flex-1 h-[1px] bg-gray-200"></div>
                  </div>

                  <!-- 3. Create new company -->
                  <div class="w-full flex justify-center">
                    <button type="button" @click="chooseSignupMode" style="border: 1.5px solid #60a5fa !important;"
                      class="w-full max-w-[310px] h-11 px-4 py-2 bg-[#f4f9ff] hover:bg-[#e8f3ff] active:bg-[#d8eaff] text-[#1877F2] rounded-full font-semibold text-normal shadow-2xs transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer select-none">
                      <v-icon color="#1877F2" small>mdi-domain</v-icon>
                      <span>Create new company</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- ========================================================================= -->
              <!-- VIEW 2: FORM AUTH (LOGIN / SIGNUP) -->
              <!-- ========================================================================= -->
              <div v-else key="form-view" class="w-full">
                <!-- Signup Component -->
                <SignupComponent v-if="authMode === 'signup'" @signup-complete="handleSignupComplete"
                  @google-login="handleGoogleLoginSuccess" @go-back="handleBackNavigation" />

                <!-- Login Component -->
                <template v-else>
                  <!-- Step 1: Login Form (Image 1 Style) -->
                  <div v-if="currentStep === 'phone'" key="login-main" class="w-full">
                    <div class="text-left mb-6">
                      <h1 class="text-heading-main font-black text-gray-900 mb-1 tracking-tight">
                        Welcome back
                      </h1>
                      <p class="text-gray-500 text-normal">
                        Log in to manage your team.
                      </p>
                    </div>

                    <!-- 1. Log in with Google (On Top) -->
                    <div class="mb-5 flex justify-center w-full">
                      <GoogleLogin @google-login-success="handleGoogleLoginSuccess" @guest-login="handleGuestLogin"
                        :show-guest-login="false" :show-divider="false" button-text="signin_with" />
                    </div>

                    <!-- or divider -->
                    <div class="w-full flex items-center justify-center mb-5">
                      <div class="flex-1 h-[1px] bg-gray-300"></div>
                      <span class="px-3 text-caption text-gray-500 font-medium lowercase">or</span>
                      <div class="flex-1 h-[1px] bg-gray-300"></div>
                    </div>

                    <!-- 2. Email or mobile number Input -->
                    <div class="mb-4">
                      <label class="block text-subheading-sm font-semibold text-gray-800 mb-1.5">
                        Email or mobile number
                      </label>
                      <input type="text" v-model="form.mobile" ref="phoneInput"
                        placeholder="Enter email or mobile number" @input="checkNumberValidity"
                        @keyup.enter="focusPasswordOrProceed"
                        class="w-full h-11 px-3.5 bg-white rounded-xl text-normal text-gray-900 outline-none transition-all placeholder:text-gray-400 auth-form-input"
                        style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;" />
                    </div>

                    <!-- 3. Password Input with Show/Hide -->
                    <div class="mb-4">
                      <div class="flex items-center justify-between mb-1.5">
                        <label class="block text-subheading-sm font-semibold text-gray-800">
                          Password
                        </label>
                      </div>
                      <div class="relative flex items-center">
                        <input :type="showPasscode ? 'text' : 'password'" v-model="form.passCode" ref="passwordInput"
                          placeholder="••••••••" @keyup.enter="handleDirectLogin"
                          class="w-full h-11 pl-3.5 pr-16 bg-white rounded-xl text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 tracking-wider auth-form-input"
                          style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;" />
                        <button type="button" @click="showPasscode = !showPasscode"
                          class="absolute right-3 text-xs font-bold text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer select-none">
                          {{ showPasscode ? 'Hide' : 'Show' }}
                        </button>
                      </div>
                    </div>

                    <!-- 4. Keep me logged in & Forgot password? -->
                    <div class="flex items-center justify-between mb-5">
                      <label class="flex items-center gap-2 cursor-pointer select-none text-xs text-gray-700">
                        <input type="checkbox" v-model="keepLoggedIn"
                          class="w-4 h-4 rounded border-gray-400 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                        <span>Keep me logged in</span>
                      </label>
                      <a href="javascript:void(0)" @click="forgotPasscode"
                        class="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline">
                        Forgot password?
                      </a>
                    </div>

                    <!-- 5. Log in Button -->
                    <button type="button" @click="handleDirectLogin" :disabled="!isNumberValid || !form.passCode"
                      class="w-full h-11 bg-[#1877F2] hover:bg-[#166fe5] active:bg-[#1465d2] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-normal shadow-sm transition-all flex items-center justify-center border-none cursor-pointer select-none mb-5">
                      <v-progress-circular v-if="isLoading" indeterminate color="white" size="18" width="2"
                        class="mr-2"></v-progress-circular>
                      <span>Log in</span>
                    </button>

                    <!-- 6. Explore demo workspace -->
                    <div @click="exploreDemoWorkspace"
                      class="w-full py-3 px-4 rounded-xl text-center text-caption text-gray-600 cursor-pointer transition-all select-none"
                      style="border: 1.5px dashed #cbd5e1 !important; background-color: #f8fafc;">
                      <span>Not ready to sign up? </span>
                      <span class="text-blue-600 font-bold hover:underline">Explore a demo workspace</span>
                    </div>

                    <!-- Hidden GoogleLogin component to handle Google OAuth callback & popup -->
                    <div class="hidden" style="display: none;">
                      <GoogleLogin @google-login-success="handleGoogleLoginSuccess" @guest-login="handleGuestLogin" />
                    </div>
                  </div>

                  <!-- Step 1.5: OTP Channel Selection -->
                  <div v-if="currentStep === 'channel-select'" key="channel-select" class="login-step">
                    <!-- Delivery Options (Horizontal Cards matching app) -->
                    <div class="d-flex gap-2 my-2">
                      <!-- SMS Option -->
                      <div @click="isSmsAvailable && (selectedChannel = 'sms')" :class="[
                        'flex-1 p-3 rounded-xl border d-flex align-center justify-space-between transition-all select-none',
                        !isSmsAvailable
                          ? 'opacity-50 bg-grey-lighten-4 border-grey-lighten-2 text-grey-darken-1 cursor-not-allowed'
                          : selectedChannel === 'sms'
                            ? 'bg-blue-50 border-blue-500 text-blue-700 font-weight-bold shadow-sm cursor-pointer'
                            : 'bg-white border-grey-lighten-1 text-grey-darken-3 cursor-pointer'
                      ]" style="border-width: 1.5px">
                        <span class="text-body-2 font-weight-bold">SMS</span>
                        <v-icon v-if="selectedChannel === 'sms' && isSmsAvailable" small
                          color="primary">mdi-check-circle</v-icon>
                      </div>

                      <!-- WhatsApp Option -->
                      <div @click="isWhatsappAvailable && (selectedChannel = 'whatsapp')" :class="[
                        'flex-1 p-3 rounded-xl border d-flex align-center justify-space-between transition-all select-none',
                        !isWhatsappAvailable
                          ? 'opacity-50 bg-grey-lighten-4 border-grey-lighten-2 text-grey-darken-1 cursor-not-allowed'
                          : selectedChannel === 'whatsapp'
                            ? 'bg-green-50 border-green-500 text-green-700 font-weight-bold shadow-sm cursor-pointer'
                            : 'bg-white border-grey-lighten-1 text-grey-darken-3 cursor-pointer'
                      ]" style="border-width: 1.5px">
                        <span class="text-body-2 font-weight-bold">WhatsApp</span>
                        <v-icon v-if="selectedChannel === 'whatsapp' && isWhatsappAvailable" small
                          color="success">mdi-check-circle</v-icon>
                      </div>

                      <!-- Email Option -->
                      <div v-if="isEmailAvailable" @click="selectedChannel = 'email'" :class="[
                        'flex-1 p-3 rounded-xl border d-flex align-center justify-space-between transition-all cursor-pointer select-none',
                        selectedChannel === 'email'
                          ? 'bg-blue-50 border-blue-500 text-blue-700 font-weight-bold shadow-sm'
                          : 'bg-white border-grey-lighten-1 text-grey-darken-3'
                      ]" style="border-width: 1.5px">
                        <span class="text-body-2 font-weight-bold">Email</span>
                        <v-icon v-if="selectedChannel === 'email'" small color="primary">mdi-check-circle</v-icon>
                      </div>
                    </div>

                    <!-- Mobile & Country Selector Card -->
                    <v-card outlined class="pa-3 mb-4 rounded-xl bg-grey-lighten-5">
                      <div v-if="selectedChannel !== 'email'" class="d-flex align-center gap-2">
                        <v-select v-model="selectedCountry" :items="countries" item-text="name" item-value="code" dense
                          hide-details style="max-width: 110px" outlined class="rounded-lg font-weight-bold">
                          <template v-slot:selection="{ item }">
                            <span>{{ item.flag }} {{ item.code }}</span>
                          </template>
                          <template v-slot:item="{ item }">
                            <span>{{ item.flag }} {{ item.name }} ({{ item.code }})</span>
                          </template>
                        </v-select>
                        <v-text-field :value="form.mobile" readonly dense hide-details outlined
                          class="rounded-lg font-weight-bold"></v-text-field>
                      </div>
                      <div v-else>
                        <v-text-field :value="maskedEmail" readonly dense hide-details outlined
                          class="rounded-lg font-weight-bold"></v-text-field>
                      </div>

                      <!-- Warning banner for non-Indian numbers -->
                      <div v-if="!isIndiaSelected"
                        class="mt-2 d-flex align-center gap-1 text-caption text-amber-800 bg-amber-50 pa-2 rounded-lg border border-amber-200">
                        <span>⚠️ SMS is unavailable for {{ selectedCountry }}. Choose WhatsApp or
                          Email.</span>
                      </div>
                    </v-card>

                    <div class="d-flex justify-space-between gap-3 mt-4">
                      <v-btn text color="grey darken-1" @click="currentStep = 'phone'">Back</v-btn>
                      <v-btn color="primary" depressed class="px-6 rounded-lg font-weight-bold"
                        @click="confirmChannelAndSendOTP" :loading="isLoading">Send Code <v-icon right
                          small>mdi-arrow-right</v-icon></v-btn>
                    </div>
                  </div>

                  <!-- Step 2: Passcode -->
                  <div v-if="currentStep === 'passcode'" key="passcode" class="login-step">
                    <div class="user-info d-flex align-center mb-4">
                      <v-avatar color="primary" size="36" class="mr-3">
                        <span class="white--text">{{ userInitials }}</span>
                      </v-avatar>
                      <div>
                        <div class="text-body-1 font-weight-medium">
                          {{ maskNumber(form.mobile) }}
                        </div>
                        <a @click="resetToFirstStep" class="text-caption text-primary cursor-pointer">Change
                          Username</a>
                      </div>
                    </div>

                    <PasscodeInput v-model="form.passCode" :autoFocus="true" @keyup.enter.native="login" />

                    <v-divider class="my-4"></v-divider>

                    <div class="d-flex flex-col sm:flex-row justify-space-between align-stretch sm:align-center gap-3">
                      <span
                        class="text-primary cursor-pointer hover-effect text-blue-500 underline text-center sm:text-left"
                        @click="forgotPasscode">
                        Forgot Passcode?
                      </span>
                      <v-btn color="primary" @click="login" :disabled="form.passCode?.length !== 6" :loading="isLoading"
                        :block="$vuetify.breakpoint.smAndDown">Login
                        <v-icon>mdi-login</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Step 3: OTP -->
                  <div v-if="currentStep === 'otp'" key="otp" class="login-step">
                    <div class="mb-4 text-center">
                      <p class="text-caption text-grey-darken-1">
                        Please confirm your request by entering the OTP sent to
                        <strong>{{ form.mobile }}</strong>
                      </p>
                    </div>

                    <PasscodeInput v-model="form.otp" :otpField="4" :autoFocus="true" @keyup.enter.native="verifyOTP" />
                    <v-divider class="my-4"></v-divider>
                    <div class="text-center mb-4">
                      <p v-if="otpCounter > 0" class="text-caption">
                        Resend OTP in <span class="font-weight-bold">{{ otpCounter }}s</span>
                      </p>
                      <span v-else class="text-primary cursor-pointer hover-effect" @click="resendOTP">
                        Resend OTP
                      </span>
                    </div>

                    <div class="d-flex flex-col-reverse sm:flex-row justify-between gap-3 mt-2">
                      <v-btn color="secondary" @click="currentStep = 'passcode'" :block="$vuetify.breakpoint.smAndDown">
                        Back
                      </v-btn>
                      <v-btn color="primary" @click="verifyOTP" :disabled="form.otp?.length !== 4" :loading="isLoading"
                        elevation="2" class="px-8" :block="$vuetify.breakpoint.smAndDown">
                        Verify
                      </v-btn>
                    </div>
                    <!-- <div class="d-flex justify-center mt-2"></div> -->
                  </div>

                  <!-- Step 4: Set New Passcode -->
                  <div v-if="currentStep === 'set-passcode'" key="set-passcode" class="login-step">
                    <div class="text-center mb-4">
                      <p class="text-caption text-grey-darken-1">
                        Create a 6-digit passcode for quick and secure login
                      </p>
                    </div>

                    <div class="mb-3">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <label class="text-caption font-weight-bold text-grey-darken-2 mb-0">Enter New Passcode</label>
                        <div class="d-flex align-center cursor-pointer text-caption text-grey-darken-1"
                          @click="showSetPasscode = !showSetPasscode" style="user-select: none;">
                          <v-icon small class="mr-1" color="grey darken-1">
                            {{ showSetPasscode ? 'mdi-eye' : 'mdi-eye-off' }}
                          </v-icon>
                          <span>{{ showSetPasscode ? 'Hide' : 'Show' }}</span>
                        </div>
                      </div>
                      <PasscodeInput v-model="form.setPasscode" :autoFocus="true" :hideToggleIcon="true"
                        :isPasswordVisible="showSetPasscode" @keyup.enter.native="setNewPasscode" />
                    </div>

                    <div class="mb-5">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <label class="text-caption font-weight-bold text-grey-darken-2 mb-0">Confirm New
                          Passcode</label>
                        <div class="d-flex align-center cursor-pointer text-caption text-grey-darken-1"
                          @click="showConfirmPasscode = !showConfirmPasscode" style="user-select: none;">
                          <v-icon small class="mr-1" color="grey darken-1">
                            {{ showConfirmPasscode ? 'mdi-eye' : 'mdi-eye-off' }}
                          </v-icon>
                          <span>{{ showConfirmPasscode ? 'Hide' : 'Show' }}</span>
                        </div>
                      </div>
                      <PasscodeInput v-model="form.confirmPasscode" :hideToggleIcon="true"
                        :isPasswordVisible="showConfirmPasscode" @keyup.enter.native="setNewPasscode" />
                    </div>

                    <div class="d-flex flex-column gap-2 justify-center">
                      <v-btn color="primary" @click="setNewPasscode" :disabled="!canSetPasscode" :loading="isLoading"
                        depressed class="h-12 rounded-xl font-weight-bold" :block="$vuetify.breakpoint.smAndDown">
                        Confirm Passcode
                      </v-btn>
                      <v-btn text color="grey darken-1" @click="currentStep = 'passcode'"
                        :block="$vuetify.breakpoint.smAndDown">
                        Back
                      </v-btn>
                    </div>
                  </div>
                </template>

                <!-- Toast Messages -->
                <div v-for="(alert, index) in alerts" :key="index" class="my-4">
                  <v-alert dense dismissible text :type="alert.type" @input="removeAlert(index)">
                    {{ alert.message }}
                  </v-alert>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div class="fixed bottom-4 right-4 z-50">
          <button type="button" @click="openComplaintCard" style="
              background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
              color: #ffffff !important;
            "
            class="w-12 h-12 rounded-full text-white shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center border-0 cursor-pointer select-none"
            title="Help Desk & Support">
            <v-icon color="white">mdi-headset</v-icon>
          </button>
          <QueryComponent v-if="isComplaintOpen" @handleCloseComplaint="openComplaintCard" />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import QueryComponent from '@/components/queryComponent.vue'
// import Designer from '@/assets/img/Designer.svg'
// import leftImage from '@/assets/img/LeftImage.svg'
// import analysisImage from '@/assets/img/Market_analysis.svg'
import Designer from '@/assets/img/image1.png'
import leftImage from '@/assets/img/image2.png'
import analysisImage from '@/assets/img/image3.png'
import PasscodeInput from '@/components/PasscodeInput.vue'
import SignupComponent from '@/components/Workforce/SignupComponent.vue'
import GoogleLogin from '@/components/GoogleLogin.vue'
import { maskPhoneNumber } from '@/utils/common'
import { version } from '../../version'
import LoginCarousel from '@/components/Workforce/LoginCarousel.vue'
import { initNotificationService, initLiveLocationService } from '@/app/utils/flutterBridge'


export default {
  name: 'LoginPage',
  layout: 'newLayout',
  components: {
    QueryComponent,
    PasscodeInput,
    SignupComponent,
    GoogleLogin,
    LoginCarousel
  },
  data() {
    return {
      landingView: 'landing', // 'landing' (3 options) or 'form' (login/signup inputs)
      authMode: 'login', // login, signup
      userType: 'employee', // 'admin' or 'employee'
      currentStep: 'phone', // phone, passcode, otp, set-passcode, channel-select
      userName: '',
      currentSlide: 0,
      otpCounter: 0,
      otpTimer: null,
      isLoading: false,
      isComplaintOpen: false,
      showPasscode: false,
      showSetPasscode: false,
      showConfirmPasscode: false,
      versionNumber: version,
      extUserLoaing: false,
      is_google_logged_in: null,
      form: {
        mobile: null,
        passCode: null,
        otp: null,
        setPasscode: null,
        confirmPasscode: null,
        username: null
      },
      alerts: [],
      isNumberValid: false,
      isEmail: false,
      isLoginId: false,
      isGoogleLoginLoading: false,
      keepLoggedIn: true,
      selectedCountry: '+91',
      selectedChannel: 'sms',
      userEmail: '',
      allowWhatsapp: false,
      allowEmail: true,
      countries: [
        { code: '+91', name: 'India', flag: '🇮🇳' },
        { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
        { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
        { code: '+977', name: 'Nepal', flag: '🇳🇵' },
        { code: '+1', name: 'United States', flag: '🇺🇸' }
      ]
    }
  },
  computed: {
    isIndianNumberByDigit() {
      const cleanNum = this.form.mobile ? String(this.form.mobile).trim().replace(/\D/g, '') : ''
      if (cleanNum.length === 10) {
        return /^[6-9]\d{9}$/.test(cleanNum)
      }
      return false
    },
    isIndiaSelected() {
      return this.selectedCountry === '+91' && this.isIndianNumberByDigit
    },
    isSmsAvailable() {
      return this.isIndiaSelected
    },
    maskedEmail() {
      const emailVal =
        this.userEmail ||
        (this.form.username && this.form.username.includes('@') ? this.form.username : '')
      if (!emailVal || !emailVal.includes('@')) return ''
      const [name, domain] = emailVal.split('@')
      if (name.length <= 2) {
        return `${name.charAt(0)}***@${domain}`
      }
      return `${name.charAt(0)}***${name.charAt(name.length - 1)}@${domain}`
    },
    isWhatsappAvailable() {
      return this.allowWhatsapp === true
    },
    isEmailAvailable() {
      const emailVal =
        this.userEmail ||
        (this.form.username && this.form.username.includes('@') ? this.form.username : '')
      const hasValidEmail = Boolean(emailVal && emailVal.trim().length > 0)
      return this.allowEmail !== false && hasValidEmail
    },
    isInIframe() {
      try {
        return window.self !== window.top
      } catch (e) {
        return true
      }
    },
    isInWebview() {
      if (typeof window === 'undefined') return false
      const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera
      return (
        /wv/i.test(userAgent) ||
        /WebView/i.test(userAgent) ||
        (/iPhone|iPod|iPad/i.test(userAgent) && !/Safari/i.test(userAgent)) ||
        (/Android/i.test(userAgent) && /Version\/[0-9.]+/i.test(userAgent))
      )
    },
    isFixedMode() {
      return this.$route?.query?.mode === 'login' || this.$route?.query?.mode === 'signup'
    },
    showLoginAsEmployee() {
      return this.isInIframe || this.isInWebview
    },
    isFromAppOrMobile() {
      const isMobileScreen = this.$vuetify.breakpoint.smAndDown
      const isAppSource =
        this.$route?.query?.from === 'app' ||
        this.$route?.query?.source === 'app' ||
        this.isInIframe ||
        this.isInWebview
      return isMobileScreen && isAppSource
    },
    slides() {
      return [
        {
          image: Designer,
          headerText: 'Best Employee Experience Platform.',
          subText:
            "Don't limit yourself. Many people limit themselves to what they think they can do. You can go as far as your mind lets you. You achieve what you believe."
        },
        {
          image: leftImage,
          headerText: 'Finally a place where it all comes together',
          subText:
            'Employees like you can even make Mondays a joy. Thanks for your hard work and super attitude.'
        },
        {
          image: analysisImage,
          headerText: 'Focus on the work that matters',
          subText: "Business opportunities are like buses, there's always another one coming."
        }
      ]
    },
    welcomeMessage() {
      switch (this.currentStep) {
        case 'phone':
          return 'Login'
        case 'passcode':
          return this.userName ? `Welcome back, ${this.userName}` : 'Enter your passcode'
        case 'otp':
          return 'Enter  OTP'
        case 'set-passcode':
          return 'Create new passcode'
        case 'channel-select':
          return 'Get OTP'
        default:
          return 'Welcome'
      }
    },
    userInitials() {
      if (this.userName) {
        const nameParts = this.userName.split(' ')
        if (nameParts && nameParts.length > 1) {
          return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
        }
        return nameParts[0][0].toUpperCase()
      }
      return ''
    },
    canSetPasscode() {
      if (
        this.form.setPasscode?.length === 6 &&
        this.form.confirmPasscode?.length === 6 &&
        this.form.setPasscode !== this.form.confirmPasscode
      ) {
        this.showToast('Confirm password and New password are not same', 'error')
      }
      return (
        this.form.setPasscode &&
        this.form.confirmPasscode &&
        this.form.setPasscode.length === 6 &&
        this.form.setPasscode === this.form.confirmPasscode
      )
    }
  },
  watch: {
    currentStep() {
      this.form.passCode = null
      this.form.setPasscode = null
      this.form.confirmPasscode = null
      this.form.otp = null
      if (this.currentStep === 'phone') {
        this.$nextTick(() => {
          this.$refs.phoneInput.focus()
        })
      }
      // this.checkNumberValidity()
    }
  },
  created() {
    if (this.$route?.query?.mode === 'signup') {
      this.authMode = 'signup'
      this.landingView = 'form'
    } else if (this.$route?.query?.mode === 'login') {
      this.authMode = 'login'
      this.landingView = 'form'
    }
    const userData = JSON.parse(localStorage.getItem('user'))
    const first_name = userData?.first_name || userData?.user?.first_name
    const mobile = userData?.mobile || userData?.user?.mobile
    const email = userData?.email || userData?.user?.email
    const username = userData?.username || userData?.user?.username || mobile || email

    if (userData && first_name && (mobile || email || username)) {
      this.userName = `${first_name || ''} ${userData?.last_name || userData?.user?.last_name || ''
        }`.trim()
      this.isEmail = userData?.isEmail || userData?.user?.isEmail || false
      this.isLoginId = userData?.isLoginId || userData?.user?.isLoginId || false

      this.form.mobile = this.isEmail || this.isLoginId ? username : mobile
      this.form.username = username
      this.currentStep = 'passcode'
      this.landingView = 'form'
    } else {
      localStorage.clear()
      localStorage.setItem('theme', 'light')
    }
  },
  methods: {
    chooseLoginMode(type) {
      this.userType = type
      this.authMode = 'login'
      this.currentStep = 'phone'
      this.landingView = 'form'
      this.$nextTick(() => {
        if (this.$refs.phoneInput) {
          this.$refs.phoneInput.focus()
        }
      })
    },
    chooseSignupMode() {
      this.authMode = 'signup'
      this.landingView = 'form'
    },
    handleBackNavigation() {
      if (this.currentStep === 'passcode' && this.userName) {
        localStorage.clear()
        localStorage.setItem('theme', 'light')
        this.userName = ''
        this.form.mobile = null
        this.form.username = null
        this.form.passCode = null
        this.landingView = 'landing'
        this.currentStep = 'phone'
        return
      }

      if (this.currentStep !== 'phone' && this.authMode === 'login') {
        this.currentStep = 'phone'
        return
      }

      this.landingView = 'landing'
      this.authMode = 'login'
      this.currentStep = 'phone'
    },
    handleBackToApp() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.switchToMobileApp()
      }
    },
    openPrivacyPolicy() {
      window.open('https://cualitywork.com/privacy', '_blank')
    },
    switchToEmployeeMode() {
      this.userType = 'employee'
      this.resetToFirstStep()
      this.showToast('Switched to Employee Login mode', 'info')
    },
    switchToAdminMode() {
      this.userType = 'admin'
      this.resetToFirstStep()
      this.showToast('Switched to Admin Login mode', 'info')
    },
    switchToMobileApp() {
      const mobileUrl = process.env.VUE_APP_MOBILE_LOGIN_URL || 'https://uatcualityapp.myfinfi.com'
      window.open(mobileUrl, '_blank')

      try {
        window.parent.postMessage({ type: 'NAVIGATE_TO_MOBILE_LOGIN' }, '*')
      } catch (e) {
        console.error(e)
      }
    },
    focusPasswordOrProceed() {
      if (this.$refs.passwordInput) {
        this.$refs.passwordInput.focus()
      } else {
        this.handleDirectLogin()
      }
    },
    async handleDirectLogin() {
      if (!this.form.mobile) {
        this.showToast('Please enter your email or mobile number', 'error')
        return
      }
      if (!this.form.passCode) {
        this.showToast('Please enter your password', 'error')
        return
      }

      this.isLoading = true
      const username = this.form.mobile.trim()
      this.form.username = username

      try {
        // First check/fetch employee & organization info from ext-user-details if organization_code is not present or user object is missing
        let orgCode = localStorage.getItem('organization_code')
        try {
          const res = await this.$axios.post('/cualitywork/ext-user-details', { username: username })
          const data = res?.data
          if (data) {
            this.is_google_logged_in = data.is_google_logged_in
            this.userEmail = data.email || ''
            this.allowWhatsapp = data.allow_whatsapp === true
            this.allowEmail = data.allow_email !== false

            if (data.organization_code) {
              orgCode = data.organization_code
              localStorage.setItem('organization_code', data.organization_code)
            }
            if (data.status) {
              if (data.mobile) {
                this.form.mobile = data.mobile
              }
              const userDetails = {
                user: {
                  first_name: data.first_name,
                  last_name: data.last_name,
                  mobile: this.form.mobile,
                  profile_image: data.profile_image,
                  username: username,
                  isEmail: this.isEmail,
                  isLoginId: this.isLoginId,
                  email: this.userEmail
                },
                role: data.role || []
              }
              this.userName = `${data.first_name || ''} ${data.last_name || ''}`.trim()
              localStorage.setItem('user', JSON.stringify(userDetails))
            }
          }
        } catch (e) {
          console.warn('Could not fetch ext-user-details prior to login:', e)
        }

        // Proceed to authenticate with username, passcode & organization_code
        await this.login(orgCode)
      } catch (error) {
        console.error(error)
      }
    },
    triggerGoogleLogin() {
      const googleBtn = document.querySelector('#google-login-button div[role="button"]')
      if (googleBtn) {
        googleBtn.click()
      } else {
        // Fallback: render or trigger google script
        this.isGoogleLoginLoading = true
        setTimeout(() => {
          this.isGoogleLoginLoading = false
          this.showToast('Google login initialized', 'info')
        }, 1000)
      }
    },
    exploreDemoWorkspace() {
      this.showToast('Redirecting to demo workspace...', 'info')
      setTimeout(() => {
        window.open('https://cualitywork.com/demo', '_blank')
      }, 500)
    },
    maskNumber(num) {
      if (!num) return
      return maskPhoneNumber(String(num))
    },
    showToast(message, type = 'info') {
      const alert = { message, type }
      this.alerts.push(alert)
      setTimeout(() => {
        this.removeAlert(this.alerts.indexOf(alert))
      }, 10000)
    },
    removeAlert(index) {
      if (index > -1) {
        this.alerts.splice(index, 1)
      }
    },
    handleSignupComplete(data) {
      // Handle both old and new formats
      if (typeof data === 'string') {
        // Old format - just mobile number
        this.authMode = 'login'
        this.form.mobile = data
        this.form.username = data
        this.currentStep = 'passcode'
      } else if (typeof data === 'object' && data.mobile && data.passcode) {
        // New format - mobile and passcode for automatic login
        this.form.mobile = data.mobile
        this.form.username = data.mobile || data.email
        this.form.passCode = data.passcode
        // this.currentStep = 'passcode'
        if (data?.organization_code) {
          localStorage.setItem('organization_code', data.organization_code)
        }
        // Set flag indicating this is a new organization that needs setup
        localStorage.setItem('needsInitialSetup', 'true')
        // Automatically login after a short delay
        setTimeout(() => {
          this.login()
        }, 1000)
      }
      // else if (typeof data === 'object' && data.google_token) {
      //   this.handleGoogleLoginSuccess({ token: data.google_token })
      // }
    },
    validateAndNext() {
      if (!this.form.mobile) {
        this.showToast('Please enter your email, phone number or login ID', 'error')
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (emailRegex.test(this.form.mobile)) {
        console.log('Email detected, Google login option available')
      }

      this.isLoading = true
      this.extUserLoaing = true

      this.$axios
        .post('/cualitywork/ext-user-details', { username: this.form.username })
        .then((res) => {
          const data = res.data
          this.is_google_logged_in = data?.is_google_logged_in
          this.userEmail = data.email || ''
          this.allowWhatsapp = data.allow_whatsapp === true
          this.allowEmail = data.allow_email !== false
          if (data.status) {
            if (data.mobile) {
              this.form.mobile = data.mobile
            }
            const userDetails = {
              user: {
                first_name: data.first_name,
                last_name: data.last_name,
                mobile: this.form.mobile,
                profile_image: data.profile_image,
                username: this.form.username,
                isEmail: this.isEmail,
                isLoginId: this.isLoginId,
                email: this.userEmail
              },
              role: data.role || []
            }
            this.userName = `${data.first_name || ''} ${data.last_name || ''}`.trim()
            if (data.organization_code) {
              localStorage.setItem('organization_code', data.organization_code)
            }
            localStorage.setItem('user', JSON.stringify(userDetails))
            console.log({ userDetails })
            if (!data.skip_otp) {
              if (data.is_google_logged_in) {
                this.showToast('Your account is created with google sign-in', 'error')
                this.extUserLoaing = false
                return
              }
              // Auto-select channel based on country & configuration
              if (this.isSmsAvailable) {
                this.selectedChannel = 'sms'
              } else if (this.isWhatsappAvailable) {
                this.selectedChannel = 'whatsapp'
              } else if (this.isEmailAvailable) {
                this.selectedChannel = 'email'
              }
              this.currentStep = 'channel-select'
            } else {
              this.currentStep = 'passcode'
            }
          } else {
            this.showToast(
              this.isLoginId
                ? 'Invalid login ID'
                : this.isEmail
                  ? 'Invalid email'
                  : 'Invalid phone number',
              'error'
            )
          }
        })
        .catch((err) => {
          console.error(err)
          this.showToast(
            this.isLoginId
              ? 'Failed to validate login ID'
              : this.isEmail
                ? 'Failed to validate email'
                : 'Failed to validate phone number',
            'error'
          )
        })
        .finally(() => {
          this.isLoading = false
          this.extUserLoaing = false
        })
    },

    async login(resolvedOrgCode) {
      if (!this.form.passCode) {
        this.showToast('Please enter your passcode', 'error')
        return
      }

      this.isLoading = true
      const orgCode = resolvedOrgCode || localStorage.getItem('organization_code')
      this.$axios
        .post('/auth/v3/login', {
          username: this.form.username,
          passcode: this.form.passCode,
          organization_code: orgCode
        })
        .then(async () => {
          this.$storage.setUniversal('isAuthenticated', 'true')
          await Promise.all([this.setUser(), this.fetchLoginUserId()])

          const userObj = this.$storage.getUniversal('user') || {}
          const userRoles = Array.isArray(userObj.role) ? userObj.role : []
          const isAdmin = userRoles.includes('WFM_ADMIN') || userRoles.includes('WFM_ACCESS')

          // Check Portal Segregation on login
          if (this.userType === 'admin') {
            if (!isAdmin) {
              this.showToast('Access denied: You do not have administrative privileges for this portal.', 'error')
              localStorage.clear()
              this.$storage.removeUniversal('isAuthenticated')
              this.$storage.removeUniversal('user')
              this.isLoading = false
              return
            }
            localStorage.setItem('app_mode', 'admin')
            sessionStorage.setItem('just_logged_in', 'true')
            sessionStorage.removeItem('tutorial_session_checked')
            const needsInitialSetup = localStorage.getItem('needsInitialSetup')
            const hasCompletedSetup = this.$storage.getUniversal('hasCompletedSetup')

            if (needsInitialSetup === 'true' && !hasCompletedSetup) {
              this.$router.replace('/workforce/setup')
            } else {
              this.$router.replace('/workforce/dashboard')
            }
          } else {
            // Employee Login portal
            localStorage.setItem('app_mode', 'employee')
            sessionStorage.setItem('just_logged_in', 'true')
            sessionStorage.removeItem('tutorial_session_checked')
            this.$router.replace('/app/dashboard')
          }
        })
        .catch((err) => {
          console.error(err)
          const errMsg = err?.response?.data?.message || 'Invalid credentials'
          this.showToast(errMsg, 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    forgotPasscode() {
      // Auto-select channel based on country & configuration
      if (this.isSmsAvailable) {
        this.selectedChannel = 'sms'
      } else if (this.isWhatsappAvailable) {
        this.selectedChannel = 'whatsapp'
      } else if (this.isEmailAvailable) {
        this.selectedChannel = 'email'
      }
      this.currentStep = 'channel-select'
    },
    resetToFirstStep() {
      this.currentStep = 'phone'
      localStorage.clear()
      localStorage.setItem('theme', 'light')
    },
    confirmChannelAndSendOTP() {
      if (this.selectedChannel === 'sms' && !this.isSmsAvailable) {
        this.showToast(
          'SMS is available only for Indian (+91) numbers starting with 6, 7, 8, or 9',
          'error'
        )
        return
      }
      if (this.selectedChannel === 'email' && !this.isEmailAvailable) {
        this.showToast('Customer email is not registered', 'error')
        return
      }
      this.sendOTP()
      this.currentStep = 'otp'
    },

    sendOTP() {
      this.isLoading = true
      const payload = {
        mobile: Number(this.form.mobile),
        channel: this.selectedChannel,
        notification_channel: this.selectedChannel,
        type: this.selectedChannel,
        country_code: this.selectedCountry,
        email: this.userEmail,
        whatsapp_config: {
          template_name: 'finfi_otp_login_verification',
          language: 'en',
          header: 'Verification Code',
          body: '{{otp}} is your verification code. For your security, do not share this code.'
        },
        email_config: {
          subject: 'FinFi Passcode Verification Code',
          template_id: 'FINFI_OTP_EMAIL_TEMPLATE',
          html_template: `<div style="font-family: Arial, sans-serif; padding: 20px; color: #333; max-width: 500px; margin: 0 auto; border: 1px solid #e0e0e0; border-radius: 12px;"><h2 style="color: #4f46e5; text-align: center;">FinFi Verification Code</h2><p>Hello,</p><p>Your verification OTP code for logging into FinFi is:</p><div style="text-align: center; margin: 24px 0;"><span style="font-size: 32px; font-weight: bold; letter-spacing: 6px; color: #4f46e5; background: #f3f4f6; padding: 12px 24px; border-radius: 8px; display: inline-block;">{{otp}}</span></div><p style="font-size: 13px; color: #6b7280;">This code is valid for 5 minutes.</p></div>`
        }
      }
      this.$axios
        .post('/cualitywork/send-login-otp', payload)
        .then(() => {
          this.showToast(`OTP sent via ${this.selectedChannel}!`, 'info')
          this.startOtpCounter()
        })
        .catch((err) => {
          console.error(err)
          const errMessage = err?.response?.data?.message || 'Failed to send OTP'
          this.showToast(errMessage, 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    resendOTP() {
      if (this.otpCounter > 0) return
      this.sendOTP()
    },

    startOtpCounter() {
      this.otpCounter = 60
      clearInterval(this.otpTimer)
      this.otpTimer = setInterval(() => {
        if (this.otpCounter > 0) {
          this.otpCounter--
        } else {
          clearInterval(this.otpTimer)
        }
      }, 1000)
    },

    verifyOTP() {
      if (!this.form.otp) {
        this.showToast('Please enter OTP', 'error')
        return
      }
      this.isLoading = true
      this.$axios
        .post('/auth/v3/verify-login-otp', {
          mobile: this.form.mobile,
          otp: this.form.otp,
          organization_code: localStorage.getItem('organization_code')
        })
        .then((res) => {
          if (res.data) {
            // this.tempToken = res.data?.access_token
            this.setUser()
            this.currentStep = 'set-passcode'
          }
        })
        .catch((err) => {
          console.error(err)
          this.showToast('Invalid OTP', 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    setNewPasscode() {
      if (this.form.setPasscode !== this.form.confirmPasscode) {
        this.showToast('Passcodes do not match', 'error')
        return
      }

      if (this.form.setPasscode.length !== 6) {
        this.showToast('Passcode must be 6 digits', 'error')
        return
      }

      this.isLoading = true
      this.$axios
        .post(
          '/cualitywork/update-passcode',
          { passcode: this.form.setPasscode }
          // { headers: { Authorization: this.tempToken } }
        )
        .then((res) => {
          if (res.status === 200) {
            this.showToast('Passcode set successfully', 'success')
            this.form.passCode = this.form.setPasscode
            this.form.setPasscode = null
            this.form.confirmPasscode = null
            this.currentStep = 'passcode'
          }
        })
        .catch((err) => {
          console.error(err)
          this.showToast('Failed to set passcode', 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    async setUser() {
      try {
        const res = await this.$axios.get('/cualitywork/auth-user')
        const existingUser = this.$storage.getUniversal('user') || {}

        const mergedData = {
          ...existingUser,
          ...res.data,
          isEmail: this.isEmail
        }

        if (existingUser.user && res.data.user) {
          mergedData.user = { ...existingUser.user, ...res.data.user }
          if (existingUser.user.profile_image && !res.data.user.profile_image) {
            mergedData.user.profile_image = existingUser.user.profile_image
          }
        }

        this.$storage.setUniversal('user', mergedData)
        this.$store.dispatch('setWfmPermissions', res.data.wfmPermissions)
        this.$store.dispatch('setWfmStatements', res.data.wfmStatements || [])
        if (res.data?.role?.includes('WFM_ADMIN') && !res.data?.wfmPermissions?.length) {
          localStorage.setItem('needsInitialSetup', 'true')
        }
      } catch (error) {
        console.error(error)
      }
    },

    async fetchLoginUserId() {
      try {
        const response = await this.$axios.get('/user')
        const userObj = response.data?.user
        const userId = userObj?._id
        const systemUserId = userObj?.systemUserId?._id || userObj?.systemUserId
        if (userId) {
          localStorage.setItem('user-id', userId)
        }
        if (systemUserId) {
          localStorage.setItem('system-user-id', systemUserId)
        }
        const userData = {
          ...this.$storage.getUniversal('user'),
          _id: userId,
          systemUserId: systemUserId || userObj?.systemUserId
        }
        this.$storage.setUniversal('user', userData)
        localStorage.setItem('user', JSON.stringify(userData))
      } catch (error) {
        console.error(error)
      }
    },
    openComplaintCard() {
      this.isComplaintOpen = !this.isComplaintOpen
    },

    checkNumberValidity() {
      if (!this.form?.mobile) {
        this.isNumberValid = false
        return
      }
      const trimmed = this.form.mobile.trim()
      if (trimmed.includes('@')) {
        this.isEmail = true
        this.isLoginId = false
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        this.isNumberValid = emailRegex.test(trimmed)
      } else if (/^\d{10}$/.test(trimmed)) {
        this.isEmail = false
        this.isLoginId = false
        this.isNumberValid = true
      } else {
        this.isEmail = false
        this.isLoginId = true
        this.isNumberValid = trimmed.length >= 3
      }
      this.form.username = trimmed
    },

    handleGoogleLoginSuccess(googleResponse) {
      // console.log('Google login successful', googleResponse)

      this.isGoogleLoginLoading = true

      this.verifyGoogleTokenWithBackend(googleResponse.token, googleResponse.user)
    },

    // Send Google token to backend for verification
    async verifyGoogleTokenWithBackend(googleToken, googleUser) {
      try {
        this.isLoading = true

        const response = await this.$axios.post('/auth/v3/login', {
          google_token: googleToken
        })

        // Handle the response from your backend
        if (response.status === 200) {
          this.$storage.setUniversal('isAuthenticated', 'true')
          await Promise.all([this.setUser(), this.fetchLoginUserId()])

          const userObj = this.$storage.getUniversal('user') || {}
          const userRoles = Array.isArray(userObj.role) ? userObj.role : []
          const isAdmin = userRoles.includes('WFM_ADMIN') || userRoles.includes('WFM_ACCESS')

          if (this.userType === 'admin') {
            if (!isAdmin) {
              this.showToast('Access denied: You do not have administrative privileges for this portal.', 'error')
              localStorage.clear()
              this.$storage.removeUniversal('isAuthenticated')
              this.$storage.removeUniversal('user')
              this.isLoading = false
              this.isGoogleLoginLoading = false
              return
            }
            localStorage.setItem('app_mode', 'admin')
            sessionStorage.setItem('just_logged_in', 'true')
            sessionStorage.removeItem('tutorial_session_checked')
            const needsInitialSetup = localStorage.getItem('needsInitialSetup')
            const hasCompletedSetup = this.$storage.getUniversal('hasCompletedSetup')

            const targetRoute =
              needsInitialSetup === 'true' && !hasCompletedSetup
                ? '/workforce/setup'
                : '/workforce/dashboard'

            this.$router.replace(targetRoute).catch((err) => {
              if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err)
              }
            })
            if (googleUser?.name) {
              this.$toast?.success ? this.$toast.success('Google login successful! Welcome ' + googleUser.name) : this.showToast('Google login successful', 'success')
            }
          } else {
            // Employee Login portal
            localStorage.setItem('app_mode', 'employee')
            sessionStorage.setItem('just_logged_in', 'true')
            sessionStorage.removeItem('tutorial_session_checked')
            this.$router.replace('/app/dashboard').catch((err) => {
              if (err.name !== 'NavigationDuplicated') console.error(err)
            })
            if (googleUser?.name) {
              this.$toast?.success ? this.$toast.success('Welcome ' + googleUser.name) : this.showToast('Login successful', 'success')
            }
          }
        } else {
          throw new Error(response.data.message || 'Authentication failed')
        }
      } catch (error) {
        console.error('Error verifying Google token with backend:', error)
        const errMsg = error?.response?.data?.message || 'Google login failed. Please try again.'
        this.showToast(errMsg, 'error')
        this.$emit('google-login-error', error)
      } finally {
        this.isLoading = false
        // Reset Google login loading state
        this.isGoogleLoginLoading = false
      }
    },
    handleGuestLogin(creds) {
      this.form.username = creds.username
      this.form.mobile = creds.username
      this.form.passCode = creds.passcode
      localStorage.setItem('organization_code', creds.organization_code)
      this.login()
    }
  },
  beforeDestroy() {
    clearInterval(this.otpTimer)
  }
}
</script>

<style scoped>
.hidden {
  display: none;
}

.gradient {
  background: #5d37bc;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
}

@media (min-width: 1200px) {
  .hidden {
    display: block;
  }
}

.carousel-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.carousel-item-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-full {
  width: 80vmin;
  height: 80vmin;
  object-fit: contain;
  object-position: center;
  text-align: start;
  margin: 0;
  align-self: center;
  justify-self: center;
}

/* Force carousel to take full height */
:deep(.v-carousel) {
  height: 100% !important;
}

:deep(.v-carousel__item) {
  height: 100% !important;
}

.text {
  padding: 30px;
  text-align: center;
}

/* New animations and transitions */
.fade-up-enter-active,
.fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-up-enter,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.4s, transform 0.4s;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.login-step {
  position: relative;
  padding: 8px 0;
}

.hover-effect {
  transition: all 0.2s ease;
}

.hover-effect:hover {
  opacity: 0.8;
  text-decoration: underline;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
}

.user-info {
  background-color: rgba(33, 150, 243, 0.05);
  border-radius: 8px;
  padding: 10px;
}

.auth-form-input {
  transition: all 0.2s ease-in-out;
}

.auth-form-input:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
}

@media (max-width: 960px) {
  .login-mobile-col {
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-start !important;
    align-items: center !important;
    min-height: 100vh !important;
    min-height: 100dvh !important;
    height: 100vh !important;
    height: 100dvh !important;
    max-height: 100vh !important;
    max-height: 100dvh !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    box-sizing: border-box !important;
    padding-top: 1.25rem !important;
    padding-bottom: 2rem !important;
    padding-left: 1.25rem !important;
    padding-right: 1.25rem !important;
  }

  .login-mobile-main {
    width: 100% !important;
    max-width: 24rem !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    margin-top: 0 !important;
    margin-bottom: auto !important;
    padding-top: 0 !important;
  }

  .login-mobile-footer {
    width: 100% !important;
    max-width: 24rem !important;
    margin-top: auto !important;
    padding-top: 1rem !important;
    text-align: center !important;
  }
}
</style>
